import React from 'react'

import { UI2FormSectionStatus, WizardSectionRegistration } from './wizard-controller'

export interface WizardProgressStep {
  id: string
  title?: string
  isActive: boolean
  isCompleted: boolean
  /** Been here at least once — tappable in the progress indicator. */
  isVisited: boolean
  /** Ever advanced past; stays true after a jump-back, so sections know they're revisited. */
  wasEverCompleted: boolean
}

/** The wizard surface a `ui2:form` publishes to its sections. */
export interface IUI2WizardContext {
  /** Parent form is in wizard mode. */
  isWizard: boolean
  /** The form's wizard mode. */
  mode: 'wizard' | 'stacked'
  /** ID of the currently active section. */
  activeSectionId: string | null
  /** Registration order = declaration order in the form's children. */
  register: (registration: Omit<WizardSectionRegistration, 'index'>) => void
  deregister: (id: string) => void
  /** Called when the user submits a section or auto-advance conditions are met. */
  advance: (sectionId: string) => void
  /** Called when the user taps Skip. */
  skip: (sectionId: string) => void
  /** Back enabled — at least one completed section on the stack. */
  canGoBack: boolean
  back: () => void
  /** Progress-indicator tap; no-op if the target hasn't been visited. */
  jumpTo: (sectionId: string) => void
  /**
   * Save-flow focusFirstError navigation; reaches unvisited sections. revealSection vs
   * jumpTo: see .claude/docs/ui2-architecture.md (Forms and the wizard).
   */
  revealSection: (sectionId: string) => void
  /** Every section in declaration order with visit/completion state. Empty in default mode. */
  progressSteps: WizardProgressStep[]
  /** Read-only status projection for sections that bind `value`. Empty in default mode. */
  sectionStatuses: Record<string, UI2FormSectionStatus>
  /** Advancing would empty the queue — relabels Next as "Complete". */
  isFinalStep: boolean
  /** Queue drained (activeId null, completed stack non-empty) — forms render success. */
  isSuccessState: boolean
  /** Silent replay of prefilled sections on load: sections show a spinner, chrome stays. */
  isReplaying: boolean
}

const DEFAULT_CTX: IUI2WizardContext = {
  isWizard: false,
  mode: 'wizard',
  activeSectionId: null,
  register: () => {},
  deregister: () => {},
  advance: () => {},
  skip: () => {},
  canGoBack: false,
  back: () => {},
  jumpTo: () => {},
  revealSection: () => {},
  progressSteps: [],
  sectionStatuses: {},
  isFinalStep: false,
  isSuccessState: false,
  isReplaying: false,
}

export const UI2WizardContext = React.createContext<IUI2WizardContext>(DEFAULT_CTX)
